// The picture side of the colour grader: one shader that does, for every pixel, the same sums as `gradePixel` in grade.js (so the thumbnails and the histogram, which are drawn
// by that function, match the picture), plus what needs neighbours or a position: sharpening, vignette and film grain, and a 3D look-up table.
import { Upscaler, program } from "../upscale/gl";
import { wheelVector } from "./grade";
import { identityLut } from "./lut";

const GRADE = `#version 300 es
precision highp float;
precision highp sampler3D;
uniform sampler2D src; uniform sampler3D lut;
uniform vec2 px; uniform vec2 size; uniform float time;
uniform float exposure; uniform float contrast; uniform float highlights; uniform float shadows; uniform float fade;
uniform float temperature; uniform float tint; uniform float saturation; uniform float vibrance; uniform float hue;
uniform float vignette; uniform float grain; uniform float sharpen; uniform float lutStrength; uniform float hasLut; uniform float intensity;
uniform vec3 vLift; uniform vec3 vGamma; uniform vec3 vGain; uniform float lutSize;
in vec2 uv; out vec4 o;
float luma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }
float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
void main() {
  vec3 orig = texture(src, uv).rgb;
  vec3 c = orig;
  if (sharpen > 0.0) {
    vec3 blur = (texture(src, uv + vec2(px.x, 0.0)).rgb + texture(src, uv - vec2(px.x, 0.0)).rgb + texture(src, uv + vec2(0.0, px.y)).rgb + texture(src, uv - vec2(0.0, px.y)).rgb) * 0.25;
    c = clamp(c + (c - blur) * sharpen * 1.5, 0.0, 1.0);
    orig = c;
  }
  if (exposure != 0.0) c = pow(pow(max(c, 0.0), vec3(2.2)) * exp2(exposure), vec3(1.0 / 2.2));
  c.r *= 1.0 + 0.25 * temperature + 0.06 * tint;
  c.g *= 1.0 - 0.12 * tint;
  c.b *= 1.0 - 0.25 * temperature + 0.06 * tint;
  float y = clamp(luma(c), 0.0, 1.0);
  c *= 1.0 + shadows * 0.6 * (1.0 - y) * (1.0 - y) + highlights * 0.6 * y * y;
  float scale = contrast >= 0.0 ? 1.0 + contrast : 1.0 + 0.7 * contrast;
  c = (c - 0.5) * scale + 0.5;
  y = clamp(luma(c), 0.0, 1.0);
  c += 0.25 * (vLift * ((1.0 - y) * (1.0 - y)) + vGamma * (4.0 * y * (1.0 - y)) + vGain * (y * y));
  y = luma(c);
  float sat = clamp(max(c.r, max(c.g, c.b)) - min(c.r, min(c.g, c.b)), 0.0, 1.0);
  c = vec3(y) + (c - vec3(y)) * (saturation * (1.0 + vibrance * (1.0 - sat)));
  if (hue != 0.0) {
    float a = radians(hue);
    float yy = luma(c);
    float i = 0.596 * c.r - 0.274 * c.g - 0.322 * c.b;
    float q = 0.211 * c.r - 0.523 * c.g + 0.312 * c.b;
    float i2 = i * cos(a) - q * sin(a);
    float q2 = i * sin(a) + q * cos(a);
    c = vec3(yy + 0.956 * i2 + 0.621 * q2, yy - 0.272 * i2 - 0.647 * q2, yy - 1.106 * i2 + 1.703 * q2);
  }
  c = c * (1.0 - 0.18 * fade) + vec3(0.1 * fade);
  c = clamp(c, 0.0, 1.0);
  if (hasLut > 0.5 && lutStrength > 0.0) {
    vec3 l = texture(lut, (c * (lutSize - 1.0) + 0.5) / lutSize).rgb;
    c = mix(c, l, lutStrength);
  }
  c = mix(orig, c, intensity);
  float d = length((uv - 0.5) * vec2(size.x / size.y, 1.0)) * 1.4;
  c *= 1.0 - vignette * smoothstep(0.35, 1.0, d) * 0.9;
  if (grain > 0.0) c += (hash(uv * size + time) - 0.5) * grain * 0.25 * (1.0 - abs(luma(c) - 0.5) * 1.2);
  o = vec4(clamp(c, 0.0, 1.0), 1.0);
}`;

export class GradeEngine extends Upscaler {
  constructor(canvas) {
    super(canvas);
    this.gradeProg = program(this.gl, GRADE);
    this.lutTex = null;
    this.lutSource = null;
    this.noLut = identityLut(2);                                                 // (a table that changes nothing, so that the shader always has a 3D texture to read)
  }

  setOutput() { /* the picture keeps the video's own size: render() sets it */ }

  uploadLut(lut) {
    if (this.lutSource === lut) return;
    const { gl } = this;
    if (this.lutTex) gl.deleteTexture(this.lutTex);
    this.lutTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_3D, this.lutTex);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage3D(gl.TEXTURE_3D, 0, gl.RGB16F, lut.size, lut.size, lut.size, 0, gl.RGB, gl.FLOAT, lut.data);
    this.lutSource = lut;
  }

  // look = { params (from mergeParams), lut: { size, data } | null }
  render(source, look) {
    const { gl } = this;
    const sw = source.videoWidth || source.naturalWidth || source.width;
    const sh = source.videoHeight || source.naturalHeight || source.height;
    if (!sw || !sh) return;
    const ow = sw - (sw % 2);                                                    // video files want an even size
    const oh = sh - (sh % 2);
    if (this.canvas.width !== ow || this.canvas.height !== oh) super.setOutput(ow, oh);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.bindTexture(gl.TEXTURE_2D, this.srcTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    const p = look.params;
    const lut = look.lut || null;
    this.uploadLut(lut || this.noLut);
    const t = typeof source.currentTime === "number" ? source.currentTime : 0;
    this.pass(this.gradeProg, null, [["src", this.srcTexture]], {
      px: [1 / sw, 1 / sh], size: [ow, oh], time: Math.floor(t * 30) * 0.37 % 97,
      exposure: p.exposure, contrast: p.contrast, highlights: p.highlights, shadows: p.shadows, fade: p.fade,
      temperature: p.temperature, tint: p.tint, saturation: p.saturation, vibrance: p.vibrance, hue: p.hue,
      vignette: p.vignette * p.intensity, grain: p.grain * p.intensity, sharpen: p.sharpen * p.intensity, lutStrength: p.lutStrength, hasLut: lut ? 1 : 0, intensity: p.intensity,
      vLift: wheelVector(p.lift), vGamma: wheelVector(p.gamma), vGain: wheelVector(p.gain), lutSize: lut ? lut.size : 2,
    }, ow, oh, (g, prog) => {
      g.activeTexture(g.TEXTURE1);
      g.bindTexture(g.TEXTURE_3D, this.lutTex);
      g.uniform1i(prog.uniforms.lut, 1);
    });
  }

  dispose() {
    if (this.lutTex) this.gl.deleteTexture(this.lutTex);
    super.dispose();
  }
}
